<template>
  <div class="survey-page eval-results">
    <div class="survey-page-header">
      <div class="survey-page-header-left">
        <div class="survey-page-icon purple">
          <i class="bi bi-clipboard-data"></i>
        </div>
        <div>
          <h5 class="survey-page-title">Activity Evaluations</h5>
          <p class="survey-page-sub">
            Ratings collected from the public evaluation form
          </p>
        </div>
      </div>
      <div v-if="responses.length" class="eval-header-actions">
        <button type="button" class="eval-btn" @click="exportCsv">
          <i class="bi bi-filetype-csv mr-1"></i> Export CSV
        </button>
        <button type="button" class="eval-btn eval-btn-xlsx" @click="exportXlsx">
          <i class="bi bi-file-earmark-spreadsheet mr-1"></i> Export XLSX
        </button>
        <button
          v-if="selected && selected.calendar_event_id"
          type="button"
          class="eval-btn eval-btn-primary"
          :disabled="releasing"
          @click="releaseCertificates"
        >
          <span v-if="releasing" class="spinner-border spinner-border-sm mr-1"></span>
          <i v-else class="bi bi-patch-check mr-1"></i>
          Release certificates
        </button>
        <span
          v-else-if="selected && selected.calendar_event_id === null"
          class="eval-cert-unavailable"
        >
          Certificates can only be released for responses linked to a calendar activity.
        </span>
      </div>
    </div>

    <div v-if="loading" class="survey-loading">
      <div class="spinner-border spinner-border-sm text-primary mr-2"></div>
      Loading...
    </div>

    <div v-else-if="!activities.length" class="survey-empty">
      <div class="survey-empty-icon"><i class="bi bi-inbox"></i></div>
      <strong>No evaluations yet</strong>
      <p class="text-muted mb-0 mt-1">
        Responses appear here once participants submit the form at
        <code>/evaluation</code>.
      </p>
    </div>

    <div v-else class="eval-layout">
      <aside class="eval-sidebar">
        <div class="sidebar-label">Activities evaluated</div>
        <button
          v-for="activity in activities"
          :key="activityKey(activity)"
          type="button"
          class="sidebar-item"
          :class="{ active: activityKey(activity) === selectedKey }"
          @click="select(activity)"
        >
          <div class="si-title">
            {{ activity.activity_title || "Not linked to an activity" }}
          </div>
          <div class="si-meta">
            <span v-if="activity.on_venue" class="survey-pill f2f">
              {{ activity.on_venue }} on venue
            </span>
            <span v-if="activity.virtual" class="survey-pill online">
              {{ activity.virtual }} virtual
            </span>
            <span>{{ activity.response_count }} resp.</span>
          </div>
        </button>
      </aside>

      <main class="eval-main">
        <div v-if="detailLoading" class="survey-loading">
          <div class="spinner-border spinner-border-sm text-primary"></div>
        </div>

        <template v-else-if="summary">
          <div class="eval-stat-row">
            <div class="eval-stat">
              <div class="es-label">Responses</div>
              <div class="es-value">{{ summary.total_responses }}</div>
            </div>
            <div class="eval-stat">
              <div class="es-label">Overall average</div>
              <div class="es-value">
                {{ display(summary.overall_average) }}<span class="es-max">/5</span>
              </div>
            </div>
            <div class="eval-stat">
              <div class="es-label">On venue / Virtual</div>
              <div class="es-value">
                {{ summary.modality["on-venue"] }} / {{ summary.modality.virtual }}
              </div>
            </div>
            <div class="eval-stat">
              <div class="es-label">Senior / PWD</div>
              <div class="es-value">
                {{ summary.demographics.senior_citizen }} /
                {{ summary.demographics.pwd }}
              </div>
            </div>
            <div class="eval-stat">
              <div class="es-label">Male / Female</div>
              <div class="es-value">
                {{ summary.demographics.male }} / {{ summary.demographics.female }}
              </div>
            </div>
          </div>

          <h6 class="eval-section-title">Average per factor</h6>
          <div class="eval-factor-list">
            <div v-for="factor in factorRows" :key="factor.id" class="eval-factor-row">
              <div class="ef-label">
                {{ factor.label }}
                <span v-if="factor.venueOnly" class="ef-tag">on venue only</span>
              </div>
              <div class="ef-bar-wrap">
                <div
                  class="ef-bar"
                  :class="barClass(factor.average)"
                  :style="{ width: barWidth(factor.average) }"
                ></div>
              </div>
              <div class="ef-score">
                <template v-if="factor.average !== null">
                  {{ factor.average.toFixed(2) }}
                  <span class="ef-count">({{ factor.responses }})</span>
                </template>
                <span v-else class="ef-none">no ratings</span>
              </div>
            </div>
          </div>

          <h6 class="eval-section-title">
            Comments
            <span class="eval-count">{{ summary.comments.length }}</span>
          </h6>
          <p v-if="!summary.comments.length" class="text-muted small mb-4">
            No comments were left for this activity.
          </p>
          <div v-else class="eval-comments">
            <div v-for="comment in summary.comments" :key="comment.id" class="eval-comment">
              <div class="ec-meta">
                <span
                  class="survey-pill"
                  :class="comment.modality === 'virtual' ? 'online' : 'f2f'"
                >
                  {{ comment.modality === "virtual" ? "Virtual" : "On venue" }}
                </span>
                <span class="ec-date">{{ formatDate(comment.submitted_at) }}</span>
              </div>
              <p class="ec-text">{{ comment.comments }}</p>
            </div>
          </div>

          <h6 class="eval-section-title">
            Respondents
            <span class="eval-count">{{ responses.length }}</span>
          </h6>
          <div class="eval-table-wrap">
            <table class="table eval-table mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Age</th>
                  <th>Sex</th>
                  <th>Senior</th>
                  <th>PWD</th>
                  <th>Mode</th>
                  <th class="text-right">Avg</th>
                  <th>Certificate</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="response in responses" :key="response.id">
                  <td class="font-weight-600">{{ response.respondent_name }}</td>
                  <td class="text-muted">{{ response.respondent_email || "—" }}</td>
                  <td>{{ response.age }}</td>
                  <td class="text-capitalize">{{ response.sex }}</td>
                  <td>{{ response.senior_citizen ? "Yes" : "No" }}</td>
                  <td>{{ response.pwd ? "Yes" : "No" }}</td>
                  <td>
                    <span
                      class="survey-pill"
                      :class="response.modality === 'virtual' ? 'online' : 'f2f'"
                    >
                      {{ response.modality === "virtual" ? "Virtual" : "On venue" }}
                    </span>
                  </td>
                  <td class="text-right">{{ display(response.average) }}</td>
                  <td>
                    <span v-if="!response.certificate" class="cert-pill none">Not issued</span>
                    <span v-else-if="response.certificate.sent_at" class="cert-pill sent">
                      <i class="bi bi-check2 mr-1"></i>Sent
                    </span>
                    <span
                      v-else-if="response.certificate.send_error"
                      class="cert-pill failed"
                      title="The send was attempted and errored."
                    >
                      <i class="bi bi-exclamation-triangle mr-1"></i>Failed
                      <button type="button" class="cert-retry" @click="resend(response.certificate)">
                        Retry
                      </button>
                    </span>
                    <span
                      v-else
                      class="cert-pill queued"
                      title="Waiting for the queue worker to send this certificate."
                    >
                      Queued
                      <button
                        type="button"
                        class="cert-retry cert-retry-queued"
                        @click="resend(response.certificate)"
                      >
                        Retry
                      </button>
                    </span>
                  </td>
                  <td class="text-muted">{{ formatDate(response.submitted_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </main>
    </div>
  </div>
</template>

<script>
import "@/css/surveys.css";
import axios from "axios";
import moment from "moment";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { EVALUATION_FACTORS } from "./factors";
import { responseRows, summaryAoa } from "./exportRows";

export default {
  name: "ActivityEvaluationResults",
  data() {
    return {
      loading: true,
      detailLoading: false,
      releasing: false,
      activities: [],
      selectedKey: null,
      selected: null,
      summary: null,
      responses: [],
    };
  },
  computed: {
    // Driven by factors.js so the results page cannot drift from the form.
    factorRows() {
      const factors = (this.summary && this.summary.factors) || {};
      return EVALUATION_FACTORS.map((factor) => {
        const row = factors[factor.id] || {};
        return {
          id: factor.id,
          label: factor.label,
          venueOnly: !!factor.venueOnly,
          average: row.average != null ? Number(row.average) : null,
          responses: row.responses || 0,
        };
      });
    },
  },
  mounted() {
    this.loadActivities();
  },
  methods: {
    activityKey(activity) {
      return activity.calendar_event_id === null
        ? "unlinked"
        : String(activity.calendar_event_id);
    },
    display(value) {
      return value == null ? "—" : Number(value).toFixed(2);
    },
    barWidth(average) {
      return average == null ? "0%" : `${(average / 5) * 100}%`;
    },
    barClass(average) {
      if (average == null) return "";
      if (average >= 4) return "good";
      if (average >= 3) return "fair";
      return "poor";
    },
    formatDate(value) {
      return value ? moment(value).format("MMM D, YYYY h:mm A") : "";
    },
    async loadActivities() {
      this.loading = true;
      try {
        const { data } = await axios.get("api/activity-evaluations/activities");
        this.activities = data.activities || [];
        if (this.activities.length) {
          const queried = this.$route.query.activity_id;
          const match = queried
            ? this.activities.find(
                (a) => String(a.calendar_event_id) === String(queried)
              )
            : null;
          this.select(match || this.activities[0]);
        }
      } catch (e) {
        toastr.error("Failed to load activity evaluations.", "Error");
      } finally {
        this.loading = false;
      }
    },
    async select(activity) {
      this.selected = activity;
      this.selectedKey = this.activityKey(activity);
      this.detailLoading = true;
      // An unlinked bucket has no id to filter on, so it can only be read as
      // the unfiltered set — which is what it is when nothing else is linked.
      const params =
        activity.calendar_event_id === null
          ? {}
          : { activity_id: activity.calendar_event_id };
      try {
        const [summary, list] = await Promise.all([
          axios.get("api/activity-evaluations/summary", { params }),
          axios.get("api/activity-evaluations", { params }),
        ]);
        this.summary = summary.data;
        this.responses = list.data.evaluations || [];
      } catch (e) {
        toastr.error("Failed to load evaluation details.", "Error");
      } finally {
        this.detailLoading = false;
      }
    },
    async releaseCertificates() {
      if (!this.selected || !this.selected.calendar_event_id) return;
      this.releasing = true;
      try {
        const { data } = await axios.post(
          `api/activity-evaluations/${this.selected.calendar_event_id}/certificates/release`
        );
        if (data.released) {
          let message = `${data.released} certificate${data.released === 1 ? "" : "s"} queued for sending.`;
          if (data.skipped) {
            message += ` ${data.skipped} skipped (already issued, or no email address).`;
          }
          toastr.success(message, "Certificates");
        } else if (data.total) {
          // Not an error, but we can't tell from `skipped` alone whether these
          // already have a certificate or simply have no email address — say
          // both rather than confidently claiming the wrong one.
          toastr.info(
            "No new certificates to release — responses were skipped because they already have one, or have no email address.",
            "Certificates"
          );
        } else {
          toastr.info("There are no responses to certify yet.", "Certificates");
        }
        await this.select(this.selected);
      } catch (e) {
        toastr.error(
          e.response?.data?.message || "Failed to release certificates.",
          "Error"
        );
      } finally {
        this.releasing = false;
      }
    },
    async resend(certificate) {
      try {
        await axios.post(`api/activity-certificates/${certificate.id}/resend`);
        toastr.success("Certificate queued again.", "Certificates");
        await this.select(this.selected);
      } catch (e) {
        toastr.error(e.response?.data?.message || "Failed to resend.", "Error");
      }
    },
    fileSlug() {
      const name = (this.selected && this.selected.activity_title) || "activity";
      return name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
    },
    exportCsv() {
      // One row per respondent with every factor as its own column — this is
      // the sheet the e-certificate run is generated from.
      const rows = responseRows(this.responses, this.formatDate);
      this.downloadCsv(Papa.unparse(rows), `evaluations-${this.fileSlug()}.csv`);
    },
    xlsxFilename() {
      return `evaluations-${this.fileSlug()}.xlsx`;
    },
    // Kept separate from exportXlsx() so the workbook can be asserted on
    // without XLSX.writeFile dropping a real file next to the test run.
    buildWorkbook() {
      // The workbook earns its keep over the CSV in two ways: it carries the
      // Summary the page shows, which a single-table CSV cannot, and it keeps
      // ratings and averages as numbers so Excel can sort and re-average them.
      const workbook = XLSX.utils.book_new();
      const activityTitle =
        (this.selected && this.selected.activity_title) || "";
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet(
          summaryAoa(this.summary, activityTitle, this.formatDate)
        ),
        "Summary"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.json_to_sheet(responseRows(this.responses, this.formatDate)),
        "Responses"
      );
      return workbook;
    },
    exportXlsx() {
      XLSX.writeFile(this.buildWorkbook(), this.xlsxFilename());
    },
    downloadCsv(csv, filename) {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, filename);
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  },
};
</script>

<style scoped>
.eval-results .survey-page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.eval-btn {
  border: 1px solid rgba(0, 0, 21, 0.12);
  background: #fff;
  border-radius: 8px;
  padding: 0.45rem 0.85rem;
  font-size: 0.82rem;
  font-weight: 600;
  color: #4a5568;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.eval-btn:hover { border-color: #7c5cff; color: #7c5cff; }

.eval-btn-xlsx:hover { border-color: #1d6f42; color: #1d6f42; }
.eval-btn-primary { border-color: rgba(124, 92, 255, 0.4); color: #7c5cff; }
.eval-btn-primary:disabled { opacity: 0.6; cursor: wait; }

.cert-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
}

.cert-pill.none { background: #f0f3f8; color: #768192; }
.cert-pill.queued { background: rgba(240, 165, 61, 0.14); color: #a86b12; }
.cert-pill.sent { background: rgba(43, 182, 163, 0.14); color: #1f8f7f; }
.cert-pill.failed { background: rgba(229, 83, 83, 0.12); color: #c53a3a; }

.cert-retry {
  margin-left: 0.35rem;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  font-weight: 700;
  color: #c53a3a;
  text-decoration: underline;
  cursor: pointer;
}

.cert-retry-queued { color: #8a5a0f; }

.eval-cert-unavailable {
  font-size: 0.78rem;
  color: #9aa5b4;
  font-style: italic;
}

.eval-layout {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 1.25rem;
  align-items: start;
}

@media (max-width: 991px) {
  .eval-layout { grid-template-columns: 1fr; }
}

.eval-sidebar {
  background: #fff;
  border: 1px solid rgba(0, 0, 21, 0.07);
  border-radius: 12px;
  padding: 0.75rem;
}

.sidebar-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9aa5b4;
  padding: 0.25rem 0.4rem 0.6rem;
}

.sidebar-item {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 10px;
  padding: 0.6rem 0.65rem;
  margin-bottom: 0.3rem;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.sidebar-item:hover { background: #f7f9fc; }
.sidebar-item.active { background: rgba(124, 92, 255, 0.08); border-color: rgba(124, 92, 255, 0.3); }

.si-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #2b3a55;
  line-height: 1.35;
}

.si-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.3rem;
  margin-top: 0.35rem;
  font-size: 0.72rem;
  color: #768192;
}

.eval-main {
  background: #fff;
  border: 1px solid rgba(0, 0, 21, 0.07);
  border-radius: 12px;
  padding: 1.25rem;
  min-width: 0;
}

.eval-stat-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.eval-stat {
  border: 1px solid rgba(0, 0, 21, 0.07);
  border-radius: 10px;
  padding: 0.7rem 0.8rem;
  background: #fbfcfe;
}

.es-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9aa5b4;
  margin-bottom: 0.3rem;
}

.es-value { font-size: 1.2rem; font-weight: 700; color: #2b3a55; line-height: 1.1; }
.es-max { font-size: 0.78rem; font-weight: 600; color: #9aa5b4; }

.eval-section-title {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #768192;
  margin: 0 0 0.85rem;
}

.eval-count {
  background: #f0f3f8;
  border-radius: 999px;
  padding: 0.05rem 0.45rem;
  font-size: 0.7rem;
  color: #4a5568;
}

.eval-factor-list { margin-bottom: 1.75rem; }

.eval-factor-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 140px 110px;
  align-items: center;
  gap: 0.75rem;
  padding: 0.4rem 0;
}

@media (max-width: 767px) {
  .eval-factor-row { grid-template-columns: minmax(0, 1fr) 80px; }
  .eval-factor-row .ef-bar-wrap { display: none; }
}

.ef-label { font-size: 0.85rem; color: #2b3a55; line-height: 1.35; }

.ef-tag {
  display: inline-block;
  margin-left: 0.35rem;
  font-size: 0.62rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #1f8f7f;
  background: rgba(43, 182, 163, 0.12);
  border-radius: 6px;
  padding: 0.1rem 0.35rem;
  vertical-align: middle;
}

.ef-bar-wrap {
  height: 8px;
  border-radius: 999px;
  background: #f0f3f8;
  overflow: hidden;
}

.ef-bar { height: 100%; border-radius: 999px; background: #9aa5b4; }
.ef-bar.good { background: #2bb6a3; }
.ef-bar.fair { background: #f0a53d; }
.ef-bar.poor { background: #e55353; }

.ef-score {
  font-size: 0.85rem;
  font-weight: 700;
  color: #2b3a55;
  text-align: right;
}

.ef-count { font-weight: 500; color: #9aa5b4; font-size: 0.75rem; }
.ef-none { font-weight: 500; color: #9aa5b4; font-size: 0.78rem; }

.eval-comments { margin-bottom: 1.75rem; }

.eval-comment {
  border: 1px solid rgba(0, 0, 21, 0.07);
  border-radius: 10px;
  padding: 0.7rem 0.85rem;
  margin-bottom: 0.5rem;
  background: #fbfcfe;
}

.ec-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.ec-date { font-size: 0.72rem; color: #9aa5b4; }
.ec-text { margin: 0; font-size: 0.88rem; color: #4a5568; line-height: 1.5; }

.eval-table-wrap { overflow-x: auto; }

.eval-table { font-size: 0.82rem; }
.eval-table thead th {
  border-top: 0;
  border-bottom: 1px solid rgba(0, 0, 21, 0.08);
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9aa5b4;
  white-space: nowrap;
}
.eval-table td { vertical-align: middle; white-space: nowrap; }
.font-weight-600 { font-weight: 600; }
</style>
